import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Badge,
  useMediaQuery,
} from "@mui/material";
import { useIntl } from "react-intl";

import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import RestoreIcon from "@mui/icons-material/Restore";

import TitledCard from "../../TitledCard";
import { MacTextField } from "../../MaskedTextField";
import { Partition, RfidCardValue, RfidReaderValue, SmartalarmUser } from "../types";
import TimeTextField, { parseValue as parseTimeValue } from "../../TimeTextField";
import { checkRfidCard, hashRfidCard, uuidv4 } from "../../../../../utils/utils";
import RfidCard from "./RfidCard";
import useEditedStyles, { useEditedColor } from "../../../../../hooks/useEditedStyles";
import { isEqual } from "lodash-es";

interface RfidReaderProps {
  index: number;
  value: RfidReaderValue;
  saved: RfidReaderValue | null;
  partitions: Partition[];
  boards: { uuid: string; name: string; deleted?: boolean }[];
  users: SmartalarmUser[];
  onChange: (index: number, value: RfidReaderValue) => void;
  disabled?: boolean;
}

const RfidReader = ({ index, value, saved, partitions, boards, users, onChange, disabled }: RfidReaderProps) => {
  const [name, setName] = useState(value.name);
  const [partition, setPartition] = useState(value.partition);
  const [exitDelay, setExitDelay] = useState(value.exitDelay.toString(10));
  const [protocol, setProtocol] = useState<string>(value.protocol || "");
  const [board, setBoard] = useState<string>(value.board);
  const [mac, setMac] = useState(value.mac || "");
  const [port, setPort] = useState<string>(value.port?.toString(10) || "");
  const [dataPort1, setDataPort1] = useState(value.ports?.[0]?.toString(10) || "");
  const [dataPort2, setDataPort2] = useState(value.ports?.[1]?.toString(10) || "");
  const [ledPort, setLedPort] = useState(value.ports?.[2]?.toString(10) || "");
  const [bellPort, setBellPort] = useState(value.ports?.[3]?.toString(10) || "");
  const [cardIds, setCardIds] = useState<string[]>([]);
  const [newCardDialogOpen, setNewCardDialogOpen] = useState(false);
  const [newCardUser, setNewCardUser] = useState("");
  const [newCardNumber, setNewCardNumber] = useState("");
  const [newCardPartition, setNewCardPartition] = useState("DEF");
  const [findCardDialogOpen, setFindCardDialogOpen] = useState(false);
  const [findCardNumber, setFindCardNumber] = useState("");
  const [foundCard, setFoundCard] = useState<{ index: number; number: string } | null>(null);
  const [notFoundDialogOpen, setNotFoundDialogOpen] = useState(false);

  const { formatMessage } = useIntl();
  const editedStyle = useEditedStyles();
  const { background: editedBackground, text: editedText } = useEditedColor();
  const smallDialog = useMediaQuery("(max-width:650px)", { noSsr: true });

  const protocols = useMemo(
    () => [
      { value: "W", name: formatMessage({ defaultMessage: "Wiegand" }) },
      { value: "C", name: formatMessage({ defaultMessage: "CAN" }) },
    ],
    [formatMessage]
  );
  const canPorts = useMemo(() => ["1", "2", "3"], []);

  const active = useMemo(() => ({ active: true }), []);

  const handlePartitionChange = useCallback(
    (e) => {
      const part = e.target.value;
      setPartition(part);
      value.partition = part;

      onChange(index, value);
    },
    [index, value, onChange]
  );

  const handleBoardChange = useCallback(
    (e) => {
      const board = e.target.value;
      setBoard(board);
      value.board = board;

      onChange(index, value);
    },
    [index, value, onChange]
  );

  const handleProtocolChange = useCallback(
    (e) => {
      const protocol = e.target.value;
      setProtocol(protocol);
      value.protocol = protocol === "_" ? null : protocol;

      onChange(index, value);
    },
    [index, value, onChange]
  );

  const handlePortChange = useCallback(
    (e) => {
      const port = e.target.value;
      setPort(port);
      value.port = parseInt(port, 10);

      onChange(index, value);
    },
    [index, value, onChange]
  );

  const openNewCardDialog = useCallback(() => {
    setNewCardUser("");
    setNewCardNumber("");
    setNewCardDialogOpen(true);
  }, []);

  const addNewCard = useCallback(
    async ({ newCardCode, newCardNumber, newCardPartition }: { newCardCode: string; newCardNumber: string; newCardPartition: string }) => {
      const hash = await hashRfidCard(newCardNumber, value.salt);
      const newCard = { uuid: uuidv4(), user: newCardCode, hash } as RfidCardValue;
      if (newCardPartition !== "DEF") {
        newCard.partition = newCardPartition;
      }
      value.cards.push(newCard);
      setCardIds(value.cards.map((card) => card.uuid));
      onChange(index, value);

      setNewCardDialogOpen(false);
    },
    [index, value, onChange]
  );

  const openFindCardDialog = useCallback(() => {
    setFindCardNumber("");
    setFindCardDialogOpen(true);
  }, []);

  const findCard = useCallback(
    async (findCardNumber: string) => {
      const index = (await Promise.all(value.cards.map(async ({ hash }) => checkRfidCard(findCardNumber, value.salt, hash)))).findIndex((i) => i);
      if (!active.active) {
        return;
      }
      if (index < 0) {
        setFoundCard(null);
        setNotFoundDialogOpen(true);
      } else {
        setFoundCard({ index, number: findCardNumber });
      }
      setFindCardDialogOpen(false);
    },
    [value, active]
  );

  const onCardValueChange = useCallback(
    (i: number, v: RfidCardValue) => {
      value.cards[i] = v;
      onChange(index, value);
    },
    [index, value, onChange]
  );

  const onDelete = useCallback(() => {
    value.deleted = true;
    onChange(index, value);
  }, [index, value, onChange]);

  const onRestore = useCallback(() => {
    delete value.deleted;
    onChange(index, value);
  }, [index, value, onChange]);

  const restoreCards = useCallback(() => {
    const savedCards = saved?.cards ?? [];
    value.cards = savedCards;
    onChange(index, value);
  }, [index, onChange, saved?.cards, value]);

  useEffect(() => {
    active.active = true;

    return () => {
      active.active = false;
    };
  }, [active]);

  useEffect(() => {
    setMac(value.mac || "");
    setName(value.name);
    setPartition(value.partition);
    setExitDelay(value.exitDelay !== undefined ? value.exitDelay.toString(10) : "");
    setProtocol(value.protocol || "");
    setBoard(value.board);
    // setPorts(value.ports?.map((port) => port?.toString(10) || "") || ["", "", "", ""]);
    setDataPort1(value.ports?.[0]?.toString(10) || "");
    setDataPort2(value.ports?.[1]?.toString(10) || "");
    setLedPort(value.ports?.[2]?.toString(10) || "");
    setBellPort(value.ports?.[3]?.toString(10) || "");
    setCardIds(value.cards.map((card) => card.uuid));
  }, [value]);

  // const overview = useMemo(() => {
  //   if (value.cards.length === 0) {
  //     return null;
  //   }
  //   return (
  //     <Grid container spacing={0.5} sx={{ padding: 1 }}>
  //       {value.cards
  //         .filter((card) => card.user)
  //         .map((card) => {
  //           const user = users.find((u) => u.uuid === card.user);
  //           const userLabel = user ? [user?.name, user.deleted && ` (${formatMessage({ defaultMessage: "deleted" })})`].filter((m) => !!m).join(" ") : "";
  //           return (
  //             <Grid item key={`mac-${card.uuid}`}>
  //               <HTChip
  //                 label={[card.deleted && `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} |`, userLabel].filter((c) => !!c).join(" ")}
  //                 code={user?.deleted ? undefined : user?.code}
  //                 icon={user?.deleted ? <PersonOffIcon /> : <SvgIcon component={RfidCardsIconIcon} />}
  //               />
  //             </Grid>
  //           );
  //         })}
  //     </Grid>
  //   );
  // }, [value, users, formatMessage]);

  const cardsView = useMemo(() => {
    return (
      <Grid container spacing={1} flexDirection="column">
        {cardIds.length === value.cards.length &&
          cardIds.map((id, i) => (
            <Grid item key={`rfid-card-${id}`} sx={{ flexDirection: "row" }}>
              <RfidCard
                index={i}
                value={value.cards[i]}
                saved={saved?.cards?.find((c) => c.uuid === value.cards[i]?.uuid) || null}
                users={users}
                partitions={partitions}
                salt={value.salt}
                onChange={onCardValueChange}
                foundCard={foundCard}
                onClearFound={() => setFoundCard(null)}
                disabled={disabled || value.deleted}
              />
            </Grid>
          ))}
        <Grid item container spacing={1}>
          <Grid item>
            <Button startIcon={<AddIcon />} onClick={openNewCardDialog} disabled={disabled || value.deleted}>
              {formatMessage({ defaultMessage: "New RFID card" })}
            </Button>
          </Grid>
          <Grid item>
            <Button startIcon={<SearchIcon />} onClick={openFindCardDialog}>
              {formatMessage({ defaultMessage: "Find RFID card by number" })}
            </Button>
          </Grid>
          {saved && !isEqual(saved.cards, value.cards) ? (
            <Grid item>
              <Button startIcon={<RestoreIcon />} onClick={restoreCards} sx={{ color: editedText }}>
                {formatMessage({ defaultMessage: "Restore the full card list" })}
              </Button>
            </Grid>
          ) : null}
        </Grid>
      </Grid>
    );
  }, [
    cardIds,
    value.cards,
    value.deleted,
    value.salt,
    openNewCardDialog,
    disabled,
    formatMessage,
    openFindCardDialog,
    saved,
    restoreCards,
    editedText,
    users,
    partitions,
    onCardValueChange,
    foundCard,
  ]);

  const editView = useMemo(() => {
    return (
      <Grid container spacing={1} sx={{ padding: 1 }} flexDirection="column">
        <Grid item container justifyContent="space-between" flexDirection="row" flexWrap="wrap-reverse">
          <Grid item xs={12} lg="auto">
            <Grid container spacing={1} flexDirection="row" flexWrap="wrap">
              <Grid item xs={12} sm={6} md={4} lg="auto">
                <TextField
                  label={formatMessage({ defaultMessage: "Name" })}
                  variant="filled"
                  value={name}
                  className={saved?.name !== name ? editedStyle.input : ""}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={() => {
                    value.name = name;
                    onChange(index, value);
                  }}
                  sx={{ margin: 0, minWidth: { lg: 200 }, maxWidth: { lg: 200 } }}
                  size="small"
                  autoComplete="off"
                  disabled={disabled || value.deleted}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg="auto">
                <FormControl
                  variant="filled"
                  size="small"
                  sx={{ margin: 0, minWidth: { lg: 200 }, maxWidth: { lg: 200 } }}
                  className={saved?.partition !== partition || partitions.find((p) => p.uuid === partition)?.deleted ? editedStyle.input : ""}
                  disabled={disabled || value.deleted}
                  fullWidth
                >
                  <InputLabel id="part-select-label">{formatMessage({ defaultMessage: "Partition" })}</InputLabel>
                  <Select labelId="part-select-label" value={partition} onChange={handlePartitionChange}>
                    <MenuItem key="part-all" value="ALL" sx={{ "ul > &": { color: "secondary.main" } }}>
                      {formatMessage({ defaultMessage: "All partitions" })}
                    </MenuItem>
                    {partitions
                      ?.filter((p) => p.uuid === partition || !p.deleted)
                      ?.map((part) => (
                        <MenuItem key={`part-${part.uuid}`} value={part.uuid} className={part.deleted ? editedStyle.withHover : ""} disabled={part.deleted}>
                          {[part.deleted ? `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} |` : "", part.code, part.name]
                            .filter((i) => !!i)
                            .join(" ")}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg="auto">
                <TimeTextField
                  label={formatMessage({ defaultMessage: "Exit delay" })}
                  variant="filled"
                  value={exitDelay}
                  className={saved?.exitDelay !== parseTimeValue(exitDelay) ? editedStyle.input : ""}
                  onChange={(e) => setExitDelay(`${e.target.value}`)}
                  onBlur={() => {
                    value.exitDelay = parseTimeValue(exitDelay);
                    onChange(index, value);
                  }}
                  sx={{ margin: 0, minWidth: { lg: 200 }, width: { lg: 200 } }}
                  size="small"
                  disabled={disabled || value.deleted}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>
          {!value.deleted && !disabled && (
            <Grid item sx={{ flex: 1, display: "flex", flexDirection: "row", justifyContent: "flex-end", alignItems: "flex-start" }}>
              <Button startIcon={<DeleteForeverOutlinedIcon />} color="error" onClick={onDelete}>
                {formatMessage({ defaultMessage: "Remove" })}
              </Button>
            </Grid>
          )}
          {value.deleted && !disabled && (
            <Grid item sx={{ flex: 1, display: "flex", flexDirection: "row", justifyContent: "flex-end", alignItems: "flex-start" }}>
              <Button color="inherit" onClick={onRestore}>
                {formatMessage({ defaultMessage: "Restore" })}
              </Button>
            </Grid>
          )}
        </Grid>
        <Grid item container spacing={1} flexDirection="row" flexWrap="wrap">
          <Grid item xs={12} sm={6} md={4} lg="auto">
            <FormControl
              variant="filled"
              size="small"
              sx={{ margin: 0, minWidth: { lg: 200 }, maxWidth: { lg: 200 } }}
              className={
                (saved ? saved.board : undefined) !== board ||
                boards
                  .filter((b) => b.deleted)
                  .map((b) => b.uuid)
                  .includes(board)
                  ? editedStyle.input
                  : ""
              }
              disabled={disabled || value.deleted}
              fullWidth
            >
              <InputLabel id="boards-select-label">{formatMessage({ defaultMessage: "Board" })}</InputLabel>
              <Select labelId="boards-select-label" value={board} onChange={handleBoardChange}>
                {boards?.map((board) => (
                  <MenuItem key={`board-${board.uuid}`} value={board.uuid} className={board.deleted ? editedStyle.withHover : ""}>
                    {[board.deleted && `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} |`, board.name].filter((i) => !!i).join(" ")}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg="auto">
            <FormControl
              variant="filled"
              size="small"
              sx={{ margin: 0, minWidth: { lg: 200 }, maxWidth: { lg: 200 } }}
              className={saved?.protocol !== protocol ? editedStyle.input : ""}
              disabled={disabled || value.deleted}
              fullWidth
            >
              <InputLabel id="protocol-select-label">{formatMessage({ defaultMessage: "Protocol" })}</InputLabel>
              <Select labelId="protocol-select-label" value={protocol} onChange={handleProtocolChange}>
                {protocols?.map((protocol) => (
                  <MenuItem key={`protocol-${protocol.value}`} value={protocol.value}>
                    {protocol.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {protocol === "C" && (
            <>
              <Grid item xs={12} sm={6} md={4} lg="auto">
                <MacTextField
                  label={formatMessage({ defaultMessage: "MAC address" })}
                  value={mac}
                  className={saved?.mac !== mac ? editedStyle.input : ""}
                  onChange={setMac}
                  onBlur={() => {
                    value.mac = mac;
                    onChange(index, value);
                  }}
                  size="small"
                  sx={{ margin: 0, minWidth: { lg: 200 }, maxWidth: { lg: 200 } }}
                  autoComplete="off"
                  disabled={disabled || value.deleted}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg="auto">
                <FormControl
                  variant="filled"
                  size="small"
                  sx={{ margin: 0, minWidth: { lg: 200 }, maxWidth: { lg: 200 } }}
                  className={saved?.port !== parseInt(port, 10) ? editedStyle.input : ""}
                  disabled={disabled || value.deleted}
                  fullWidth
                >
                  <InputLabel id="protocol-select-label">{formatMessage({ defaultMessage: "Port" })}</InputLabel>
                  <Select labelId="protocol-select-label" value={port} onChange={handlePortChange}>
                    {canPorts?.map((port) => (
                      <MenuItem key={`protocol-${port}`} value={port}>
                        {port}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}
          {protocol === "W" && (
            <>
              <Grid item xs={12} sm={6} md={4} lg="auto">
                <TextField
                  type="number"
                  label={formatMessage({ defaultMessage: "Data port #1" })}
                  variant="filled"
                  value={dataPort1}
                  className={(saved?.ports?.[0] || dataPort1) && saved?.ports?.[0]?.toString(10) !== dataPort1 ? editedStyle.input : ""}
                  onChange={(e) => setDataPort1(e.target.value)}
                  onBlur={() => {
                    if (!value.ports) {
                      value.ports = [null, null, null, null];
                    }
                    value.ports[0] = dataPort1 ? parseInt(dataPort1, 10) : null;
                    onChange(index, value);
                  }}
                  sx={{ margin: 0, minWidth: { lg: 200 }, maxWidth: { lg: 200 } }}
                  size="small"
                  autoComplete="off"
                  disabled={disabled || value.deleted}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg="auto">
                <TextField
                  type="number"
                  label={formatMessage({ defaultMessage: "Data port #2" })}
                  variant="filled"
                  value={dataPort2}
                  className={(saved?.ports?.[1] || dataPort2) && saved?.ports?.[1]?.toString(10) !== dataPort2 ? editedStyle.input : ""}
                  onChange={(e) => setDataPort2(e.target.value)}
                  onBlur={() => {
                    if (!value.ports) {
                      value.ports = [null, null, null, null];
                    }
                    value.ports[1] = dataPort2 ? parseInt(dataPort2, 10) : null;
                    onChange(index, value);
                  }}
                  sx={{ margin: 0, minWidth: { lg: 200 }, maxWidth: { lg: 200 } }}
                  size="small"
                  autoComplete="off"
                  disabled={disabled || value.deleted}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg="auto">
                <TextField
                  type="number"
                  label={formatMessage({ defaultMessage: "Led port" })}
                  variant="filled"
                  value={ledPort}
                  className={(saved?.ports?.[2] || ledPort) && saved?.ports?.[2]?.toString(10) !== ledPort ? editedStyle.input : ""}
                  onChange={(e) => setLedPort(e.target.value)}
                  onBlur={() => {
                    if (!value.ports) {
                      value.ports = [null, null, null, null];
                    }
                    value.ports[2] = ledPort ? parseInt(ledPort, 10) : null;
                    onChange(index, value);
                  }}
                  sx={{ margin: 0, minWidth: { lg: 200 }, maxWidth: { lg: 200 } }}
                  size="small"
                  autoComplete="off"
                  disabled={disabled || value.deleted}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg="auto">
                <TextField
                  type="number"
                  label={formatMessage({ defaultMessage: "Bell port" })}
                  variant="filled"
                  value={bellPort}
                  className={(saved?.ports?.[3] || bellPort) && saved?.ports?.[3]?.toString(10) !== bellPort ? editedStyle.input : ""}
                  onChange={(e) => setBellPort(e.target.value)}
                  onBlur={() => {
                    if (!value.ports) {
                      value.ports = [null, null, null, null];
                    }
                    value.ports[3] = bellPort ? parseInt(bellPort, 10) : null;
                    onChange(index, value);
                  }}
                  sx={{ margin: 0, minWidth: { lg: 200 }, maxWidth: { lg: 200 } }}
                  size="small"
                  autoComplete="off"
                  disabled={disabled || value.deleted}
                  fullWidth
                />
              </Grid>
            </>
          )}
        </Grid>
        <Grid item>
          <Grid container spacing={1} flexDirection="column">
            <Grid item>
              <Typography variant="subtitle2">{formatMessage({ defaultMessage: "RFID cards" })}</Typography>
            </Grid>
            <Grid item>{cardsView}</Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }, [
    formatMessage,
    name,
    saved,
    editedStyle.input,
    editedStyle.withHover,
    disabled,
    value,
    partition,
    partitions,
    handlePartitionChange,
    exitDelay,
    onDelete,
    onRestore,
    board,
    boards,
    handleBoardChange,
    protocol,
    handleProtocolChange,
    protocols,
    mac,
    port,
    handlePortChange,
    canPorts,
    dataPort1,
    dataPort2,
    ledPort,
    bellPort,
    cardsView,
    onChange,
    index,
  ]);

  const newCardDialog = useMemo(
    () => (
      <Dialog open={newCardDialogOpen} onClose={() => setNewCardDialogOpen(false)}>
        <DialogTitle>{formatMessage({ defaultMessage: "New RFID card" })}</DialogTitle>
        <DialogContent>
          {/* <DialogContentText>
              A kártyaszám kódolva lesz eltárolva. <span style={{ color: "red" }}>(MI LEGYEN IDE ÍRVA?)</span>
            </DialogContentText> */}
          <Grid container spacing={1}>
            <Grid item xs={smallDialog ? 12 : "auto"}>
              <FormControl
                variant="filled"
                size="small"
                sx={{ margin: 0, minWidth: smallDialog ? undefined : 180, maxWidth: smallDialog ? undefined : 180 }}
                disabled={users.filter((u) => !u.deleted).length === 0}
                fullWidth
              >
                <InputLabel id="select-label">{formatMessage({ defaultMessage: "User" })}</InputLabel>
                <Select labelId="select-label" value={newCardUser} onChange={(e) => setNewCardUser(e.target.value)}>
                  {users
                    .filter((u) => !u.deleted)
                    .map((option) => (
                      <MenuItem key={`option-${option.uuid}`} value={option.uuid}>
                        {option.code} {option.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={smallDialog ? 12 : "auto"}>
              <TextField
                label={formatMessage({ defaultMessage: "Number" })}
                variant="filled"
                value={newCardNumber}
                onChange={(e) => setNewCardNumber(e.target.value)}
                sx={{ margin: 0, minWidth: smallDialog ? undefined : 160, maxWidth: smallDialog ? undefined : 160 }}
                size="small"
                autoComplete="off"
                fullWidth={smallDialog}
              />
            </Grid>

            <Grid item xs={smallDialog ? 12 : "auto"}>
              <FormControl
                variant="filled"
                size="small"
                sx={{ margin: 0, minWidth: smallDialog ? undefined : 180, maxWidth: smallDialog ? undefined : 180 }}
                disabled={partitions.length === 0}
                fullWidth
              >
                <InputLabel id="select-label">{formatMessage({ defaultMessage: "Partition" })}</InputLabel>
                <Select labelId="select-label" value={newCardPartition} onChange={(e) => setNewCardPartition(e.target.value)}>
                  <MenuItem key={`partition-none`} value={"DEF"} sx={{ fontStyle: "italic" }}>
                    {formatMessage({ defaultMessage: "Default of the reader" })}
                  </MenuItem>
                  <MenuItem key="part-all" value="ALL" sx={{ "ul > &": { color: "secondary.main" } }}>
                    {formatMessage({ defaultMessage: "All partitions" })}
                  </MenuItem>
                  {partitions
                    .filter((p) => p.uuid === newCardPartition || !p.deleted)
                    .map((option) => (
                      <MenuItem key={`partition-${option.uuid}`} value={option.uuid}>
                        {[option.deleted ? `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} |` : "", option.code, option.name]
                          .filter((i) => !!i)
                          .join(" ")}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewCardDialogOpen(false)}>{formatMessage({ defaultMessage: "Cancel" })}</Button>
          <Button disabled={!newCardNumber} onClick={() => addNewCard({ newCardCode: newCardUser, newCardNumber, newCardPartition })}>
            {formatMessage({ defaultMessage: "Add" })}
          </Button>
        </DialogActions>
      </Dialog>
    ),
    [users, partitions, newCardUser, newCardNumber, newCardPartition, addNewCard, newCardDialogOpen, formatMessage, smallDialog]
  );

  const findCardDialog = useMemo(
    () => (
      <Dialog open={findCardDialogOpen} onClose={() => setFindCardDialogOpen(false)}>
        <DialogTitle>{formatMessage({ defaultMessage: "Find RFID card by number" })}</DialogTitle>
        <DialogContent>
          <TextField
            label={formatMessage({ defaultMessage: "Number" })}
            variant="filled"
            value={findCardNumber}
            onChange={(e) => setFindCardNumber(e.target.value)}
            sx={{ margin: 0 }}
            fullWidth
            size="small"
            autoComplete="off"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFindCardDialogOpen(false)}>{formatMessage({ defaultMessage: "Cancel" })}</Button>
          <Button onClick={() => findCard(findCardNumber)}>{formatMessage({ defaultMessage: "Find" })}</Button>
        </DialogActions>
      </Dialog>
    ),
    [findCardNumber, findCard, findCardDialogOpen, formatMessage]
  );

  const notFoudDialog = useMemo(
    () => (
      <Dialog open={notFoundDialogOpen} onClose={() => setNotFoundDialogOpen(false)}>
        <DialogTitle>{formatMessage({ defaultMessage: "RFID card number not found" })}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {formatMessage({ defaultMessage: "No results for RFID card number: {number}" }, { number: findCardNumber })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotFoundDialogOpen(false)}>{formatMessage({ defaultMessage: "OK" })}</Button>
        </DialogActions>
      </Dialog>
    ),
    [notFoundDialogOpen, findCardNumber, formatMessage]
  );

  return (
    <>
      <TitledCard
        color={value.deleted ? editedBackground : undefined}
        contrastColor={value.deleted ? editedText : undefined}
        modes={[
          // {
          //   icon: <RemoveRedEyeOutlinedIcon fontSize="small" />,
          //   content: overview,
          //   title:
          //     (value.deleted ? `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} | ` : "") +
          //     formatMessage({ defaultMessage: "RFID reader" }) +
          //     (value.mac || value.name ? ` | ${[value.name, value.mac ? formatMac(value.mac) : ""].filter((i) => !!i).join(" ")}` : ""),
          // },
          {
            icon:
              disabled || value.deleted ? (
                <Badge
                  badgeContent={<RemoveRedEyeOutlinedIcon fontSize="inherit" />}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                >
                  <EditOutlinedIcon fontSize="small" />
                </Badge>
              ) : (
                <EditOutlinedIcon fontSize="small" />
              ),
            content: editView,
            title: (value.deleted ? `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} | ` : "") + formatMessage({ defaultMessage: "RFID reader" }),
          },
        ]}
        // activeMode={newItem ? 1 : 0}
      />
      {newCardDialog}
      {findCardDialog}
      {notFoudDialog}
    </>
  );
};

export default RfidReader;
